import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import * as _ from 'lodash';
import { Observable ,  Subscription } from 'rxjs';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import * as utils from 'utils/utils';
import * as dateUtils from 'utils/date';

import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { BrandService } from 'shared/brand/brand.service';
import { setItemDetailValidation } from 'shared/item-detail/item-detail-validation';
import { getValidationByName, getValidationByKey } from 'shared/validation/validation-helpers';

import { BaseSegmentMixin } from '../../../product-details/components/base/_base-segment-mixin';
import { ECOMMERCE_PANEL } from '../../product-panel-types';
import { ProductPanelService } from '../../product-panel.service';
import { SearchModel } from 'shared/search/search.model';
import { SearchService } from 'shared/search/search.service';


@Component({
  template: '',
})
export class BaseSegmentComponent extends BaseSegmentMixin implements OnDestroy {

  // bulk edit inputs
  @Input() bulkEdit: boolean = false;
  @Input() selectedIds: Array<any>;
  @Input() searchModel: SearchModel;
  @Input() listId: string;
  @Input() allSelected: boolean;

  @Input() panelType: string;

  @Output() itemDetailFetched: EventEmitter<any> = new EventEmitter();

  segmentName: string;

  itemDetailsData: any;
  itemDetailValidation: any = {};
  panelTypeFields: any = {};
  panelTypesList: any = {
    'ecommerce': ECOMMERCE_PANEL,
  };
  isActive: boolean = true;
  itemDetailSub: Subscription;

  // override segmentForm getter for every segment
  get segmentForm(): any {
    return 'need to define segmentForm!';
  }

  get isDirty(): UntypedFormGroup {
    return this.segmentForm ? this.segmentForm.dirty : false;
  }

  get controlsPlaceholder(): string {
    return this.bulkEdit ? '--' : '';
  }

  get lookupPlaceholder(): string {
    return this.bulkEdit ? '--' : '-';
  }

  get optionPlaceholder(): any {
    return {option: '', title: this.lookupPlaceholder};
  }

  get bulkDetailsData(): any {
    return JSON.parse(localStorage.getItem('bulkDetailsData')) || {};
  }

  set bulkDetailsData(data: any) {
    localStorage.setItem('bulkDetailsData', JSON.stringify(data));
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get itemId(): number {
    return this.route.snapshot.params.id;
  }

  constructor(
    protected productPanelService: ProductPanelService,
    protected brandService: BrandService,
    protected itemDetailService: ItemDetailService,
    protected searchService: SearchService,
    protected route: ActivatedRoute,
    protected ref: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnDestroy() {
    if (this.itemDetailSub) { this.itemDetailSub.unsubscribe(); }
    this.itemDetailsData = null;
    this.itemDetailValidation = {};
    this.panelTypeFields = {};
    this.isActive = false;
    this.data = null;
  }

  // override save, setData, setBulkData
  // handleItemDetail, markAsPristine, subscribeToItemDetail methods
  subscribeToItemDetail() {
    if (this.bulkEdit) {
      _.defer(() => {
        this.setBulkData();
        this.ref.markForCheck();
      });
    } else {
      this.itemDetailSub = this.itemDetailService.fetchItemDetail(this.brandService.getBrand(), this.itemId)
      .subscribe( (itemDetail) => {
        this.handleItemDetail(itemDetail);
        this.setSegmentsValidation(itemDetail);
        this.itemDetailFetched.emit(itemDetail);
        this.ref.markForCheck();
      });
    }
  }

  save(props?: any) {
    console.warn('save method not implemented!');
  }

  setData(data: any) {
    console.warn('setData method not implemented!');
  }

  setBulkData() {
    this.setData(this.getBulkSegmentData(this.segmentName));
  }

  handleItemDetail(itemDetail: any) {
    console.warn('handleItemDetail method not implemented!');
  }

  markAsPristine() {
    if (!this.segmentForm) {
      return console.warn('segmentForm is not defined!');
    }
    this.segmentForm.markAsPristine();
  }

  markAsDirty() {
    if (!this.segmentForm) {
      return console.warn('segmentForm is not defined!');
    }
    this.segmentForm.markAsDirty();
  }

  cancel() {
    this.setData(this.itemDetailsData);
    this.markAsPristine();
  }

  hasError(field: any, formGroup: UntypedFormGroup | AbstractControl): boolean {
    return utils.hasError(field, formGroup);
  }

  hasServerError(path: string, language: string= null): boolean {
    return utils.hasServerError(this.itemDetailValidation, path, language);
  }

  fieldValidation(item: any, formGroup: UntypedFormGroup): any {
    if (!item.validation || !formGroup) { return []; }
    const control = formGroup.controls[item.field_map];
    return this.getValidations(item, control as UntypedFormControl);
  }

  fieldValidationControl(item: any, formControl: UntypedFormControl): any {
    if (!item.validation || !formControl) { return []; }
    return this.getValidations(item, formControl);
  }

  fieldErrorDetailControl(item: any, formControl: UntypedFormControl) {
    if (!item || !item.title || !formControl) { return ''; }
    if (formControl.valid) { return ''; }
    return _.get(item, 'validation.PIES_6_5.message', '');
  }
  fieldErrorDetail(item: any, formGroup: UntypedFormGroup) {
    if (!item || !item.title || !formGroup) { return ''; }
    if (formGroup.controls[item.field_map].valid) { return ''; }
    return _.get(item, 'validation.PIES_6_5.message', _.get(item, 'validation.pies65.message', ''));
  }

  getItemProperty(item: any, path?: string, data?: any): any {
    return utils.getItemProperty(item, path, data || this.itemDetailsData);
  }

  getFieldValue(item: any, field: any): string {
    return utils.getFieldValue(item, field);
  }

  getFieldByName(name: string, segmentData: Array<any>): any {
    return utils.getFieldByName(name, segmentData);
  }

  getFieldByMap(map: string, segmentData: Array<any>): any {
    return utils.getFieldByMap(map, segmentData);
  }

  getItemValidators(itemValidation: any, bind?: any): any {
    return utils.getItemValidators(itemValidation, bind);
  }

  getLookupTitle(option: string, lookup: Array<any>): any {
    return utils.getLookupTitle(option, lookup);
  }

  getLookup(name: string, data: any): any {
    return utils.getLookup(name, data);
  }

  getMaxLength(item: any): number {
    return utils.getMaxLength(item);
  }

  initValidators(validation: any, bind?: any): any[] {
    return utils.initValidators(validation, bind);
  }

  addLookupPlaceholder(lookup: Array<any>): any {
    if (lookup.length) {
      if (!lookup[0].option) {
        lookup[0].title = this.lookupPlaceholder;
        return lookup;
      }
    }
    lookup.unshift(this.optionPlaceholder);
    return lookup;
  }

  getSegmentsWithPlaceholder(_segments: any) {
    const cloned = _.cloneDeep(_segments);
    _.each(cloned, (item: any) => {
      if (item.lookup) { this.addLookupPlaceholder(item.lookup); }
    });
    return cloned;
  }

  putDataBulk(segment: string, value: any, storedData?: any): Observable<any> {
    this.storeBulkData(segment, storedData || value);
    let filter = '';

    if (this.allSelected) {
      this.searchService.searchModel = this.searchModel;
      filter = '&' + this.searchService.getQueryString(this.searchModel, true);
    }
    console.log('Bulk Edit', 'putDataBulk');
    return this.itemDetailService.postSegmentDataBulk(
      this.brandService.getBrand(),
      this.selectedIds,
      segment,
      value,
      (this.allSelected ? filter : null),
      (this.allSelected ? this.listId : '')
    );
  }

  putData(id: any, segment: string, value: any): Observable<any> {
    return this.itemDetailService.putSegmentData(
      this.brandService.getBrand(),
      id,
      segment,
      value
    );
  }

  getBulkSegmentData(segment: string): any {
    return _.get(this.bulkDetailsData, segment);
  }

  setDateDisabledRange(date: string, dateItemName: string): void {
    dateUtils.setDateDisabledRange(date, dateItemName, (<any>this).data);
  }

  public setDateAheadControlError(startDateControl: AbstractControl, endDateControl: AbstractControl): void {
    dateUtils.setDateAheadControlError(startDateControl, endDateControl);
  }

  protected orderBy(data: any, property: string = 'sequence'): any {
    if (!data || !data.length) { return; }
    return _.orderBy(data, property);
  }

  protected storeBulkData(segment: string, value: any): void {
    const data = this.bulkDetailsData;
    _.set(data, segment, value);
    this.bulkDetailsData = data;
  }

  protected camelCaseToDash(str: string): string {
    return _.snakeCase(str);
  }

  protected replaceFieldMapOnItemName(data: any, segmentData: any): any {
    return _.map(_.cloneDeep(data), (item) => {
      _.each(item, (value, key) => {
        _.set(item, this.getFieldByMap(key, segmentData).item_name, value);
      });
      return item;
    });
  }

  protected getPanelSegmentsData(segmentsData: any): any {
    switch (this.panelType) {
      case ECOMMERCE_PANEL:
        if (!this.panelTypeFields[ECOMMERCE_PANEL]) { return this.orderBy(segmentsData); }
        return this.orderBy(
          _.filter(segmentsData, (item: any) => {
            return _.includes(this.panelTypeFields[ECOMMERCE_PANEL], item.item_name);
          })
        );
      default:
        return this.orderBy(segmentsData);
    }
  }

  protected setSegmentsValidation(itemDetail: any) {
    const itemDetailWithValidation = setItemDetailValidation(itemDetail);
    this.itemDetailValidation = itemDetailWithValidation.validation;
  }

  // private

  private getValidations(item: any, control: UntypedFormControl) {
    const validations = _.map(item.validation, (value: any, key: string) => {
      /*
       * TODO: We need to readdress how handle validating different types.
       */
      const validationItem: any = getValidationByName(key) || getValidationByKey(key);
      return {
  //      title: key,
        key: validationItem?.key,
        title: validationItem?.title,
        valid: control.valid,
      };
    });
    if (!validations.length) { return []; }
    // PIES 6.7 validation row was added for test
    return [
      ...validations,
      {
        key: 'pies67',
        title: 'PIES 6.7',
        valid: validations[0].valid,
      }
    ];
  }
}
